/**
 * HeatmapRangeBrush — minimap + draggable handles for time-range selection
 * on the Liquidity Map heatmap.
 *
 * 2026-02-17 v1: initial implementation, single canvas.
 * 2026-02-17 v2: CoinGlass-style refinement — chunkier 12px handles with
 *   hover/drag glow, plus a row of evenly-spaced time-tick labels above
 *   the canvas (uses the same `toLocaleString([], …)` pattern already
 *   used in HeatmapCanvas:677 and the cascade drawers, so locale follows
 *   the browser without a new helper).
 *
 * Pure presentational, additive, no fetch. Emits `[startIdx, endIdx]`
 * (inclusive, 0-based against the full `candles` array) via `onChange`.
 * `null` value = full range. Reset is owned by the parent (LiqMapPage
 * calls onResetView on coin/timeframe change and on the ↺ button).
 */
import { useEffect, useMemo, useRef, useState } from "react";

// Visual handle width. Bumped from 6→12 (2026-02-17 v2) so the handles
// match the chunky CoinGlass-style references and are easy to grab on
// touch screens. The hit-area is bumped accordingly inside onPointerDown.
const HANDLE_W = 12;
const HANDLE_HIT_PX = 14; // ≥ HANDLE_W/2 + slop, keeps grab generous.
const MIN_RANGE = 3;      // candles — keeps the heatmap renderable.
const LABEL_ROW_H = 14;   // px reserved above the canvas for the labels.
const CANVAS_H = 48;
// Time-axis tick algorithm (2026-02-17 v3 — CoinGlass-style dynamic
// spacing). We target ~TARGET_TICKS labels, but the real count is
// derived from (a) snapping the raw interval to the closest "nice"
// duration so labels land on clean clock boundaries (:00, :30, etc.),
// and (b) dropping any tick whose screen-space distance from its
// predecessor falls below MIN_TICK_PX. This gives a stable density
// across short and long candle windows without manual tuning per tf.
const TARGET_TICKS = 9;
// Minimum screen-space distance between adjacent tick CENTRES. Each
// label is ~95-110px wide (e.g. "Jun 24, 04:00 PM") so we need the
// centres at least one full label apart for the next one to fit
// without overlap. 110px keeps a comfortable safety margin against
// the longest forms ("Jun 24, 12:00 AM").
const MIN_TICK_PX = 110;
const NICE_INTERVALS_MS = [
  15 * 60 * 1000,         // 15 min
  30 * 60 * 1000,         // 30 min
  60 * 60 * 1000,         // 1 h
  2 * 60 * 60 * 1000,     // 2 h
  3 * 60 * 60 * 1000,     // 3 h
  4 * 60 * 60 * 1000,     // 4 h
  6 * 60 * 60 * 1000,     // 6 h
  8 * 60 * 60 * 1000,     // 8 h
  12 * 60 * 60 * 1000,    // 12 h
  24 * 60 * 60 * 1000,    // 1 d
  2 * 24 * 60 * 60 * 1000,
  3 * 24 * 60 * 60 * 1000,
  7 * 24 * 60 * 60 * 1000,
];
// All NICE_INTERVALS_MS values divide 24h cleanly, so anchoring with
// `Math.ceil(firstT / interval) * interval` always lands on :00 or :30
// boundaries — no more arbitrary minute offsets like 13:42.
const pickNiceInterval = (rawMs) => {
  if (rawMs <= 0) return NICE_INTERVALS_MS[0];
  // Log-distance avoids bias toward small intervals (linear distance
  // would always favour 15min for any short range).
  let best = NICE_INTERVALS_MS[0];
  let bestDist = Math.abs(Math.log(best) - Math.log(rawMs));
  for (const v of NICE_INTERVALS_MS) {
    const d = Math.abs(Math.log(v) - Math.log(rawMs));
    if (d < bestDist) { best = v; bestDist = d; }
  }
  return best;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const fmtTick = (ms) => {
  if (!Number.isFinite(ms)) return "";
  try {
    return new Date(ms).toLocaleString([], {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function HeatmapRangeBrush({ candles = [], value, onChange }) {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [dragging, setDragging] = useState(null); // 'left' | 'right' | 'window' | null
  const [hoverHandle, setHoverHandle] = useState(null); // 'left' | 'right' | null
  const dragStateRef = useRef(null);

  const n = candles.length;
  const start = value ? value[0] : 0;
  const end = value ? value[1] : Math.max(0, n - 1);

  // Time ticks — equispaced indices into the FULL candles array (not the
  // selected subset, on purpose: the brush shows the whole timeline so
  // its ticks always cover the whole timeline too).
  // Dynamic time ticks — picks a "nice" interval matching the visible
  // range, anchors to clean clock boundaries (:00 / :30), then prunes
  // any labels that would visually overlap at the current width. The
  // input range tracks the active brush selection: when the user is
  // looking at the full window, ticks span the full 60h/60d/etc.; when
  // they shrink the brush to a 10-candle slice, the same algorithm
  // recomputes a finer interval against those 10 candles' timestamps.
  // The algorithm itself (NICE_INTERVALS_MS, snap, anchor, overlap
  // pruning) is unchanged — only firstT/lastT vary.
  const tickLabels = useMemo(() => {
    if (n < 2) return [];
    const lo = start;
    const hi = end;
    if (hi <= lo) return [];
    const firstT = candles[lo]?.t;
    const lastT = candles[hi]?.t;
    if (!Number.isFinite(firstT) || !Number.isFinite(lastT) || lastT <= firstT) {
      return [];
    }
    const totalMs = lastT - firstT;
    const rawInterval = totalMs / (TARGET_TICKS - 1);
    const interval = pickNiceInterval(rawInterval);
    const startTs = Math.ceil(firstT / interval) * interval;
    const raw = [];
    for (let t = startTs; t <= lastT; t += interval) {
      // Restrict the closest-idx search to the active sub-range so
      // ticks never wander outside the highlighted window.
      let bestIdx = lo;
      let bestDelta = Math.abs(candles[lo].t - t);
      for (let i = lo + 1; i <= hi; i += 1) {
        const d = Math.abs(candles[i].t - t);
        if (d < bestDelta) { bestIdx = i; bestDelta = d; }
      }
      raw.push({ idx: bestIdx, t, label: fmtTick(t) });
    }
    // Overlap protection in screen space.
    if (!width || raw.length <= 1) return raw;
    const xOf = (idx) => (idx / Math.max(1, n - 1)) * width;
    const kept = [raw[0]];
    for (let i = 1; i < raw.length; i += 1) {
      if (xOf(raw[i].idx) - xOf(kept[kept.length - 1].idx) >= MIN_TICK_PX) {
        kept.push(raw[i]);
      }
    }
    return kept;
  }, [candles, n, width, start, end]);

  // Responsive width tracking.
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setWidth(Math.max(0, Math.floor(cr.width)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  // Normalised closes for the sparkline (0=top, 1=bottom).
  const linePoints = useMemo(() => {
    if (!n) return [];
    const closes = candles.map((c) => Number(c.c) || 0);
    const lo = Math.min(...closes);
    const hi = Math.max(...closes);
    const span = hi - lo || 1;
    return closes.map((c) => 1 - (c - lo) / span);
  }, [candles, n]);

  // Draw the brush canvas.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !width || !n) return;
    const h = CANVAS_H;
    const dpr = window.devicePixelRatio || 1;
    cv.width = width * dpr;
    cv.height = h * dpr;
    cv.style.width = `${width}px`;
    cv.style.height = `${h}px`;
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, h);

    // Backdrop.
    ctx.fillStyle = "rgba(8,5,8,0.6)";
    ctx.fillRect(0, 0, width, h);

    // Faint vertical guides aligned to the time ticks above.
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    ctx.lineWidth = 1;
    tickLabels.forEach(({ idx }) => {
      const x = (idx / Math.max(1, n - 1)) * width;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, h);
      ctx.stroke();
    });

    // Full sparkline (muted).
    ctx.beginPath();
    linePoints.forEach((y, i) => {
      const x = (i / Math.max(1, n - 1)) * width;
      const py = 3 + y * (h - 6);
      if (i === 0) ctx.moveTo(x, py);
      else ctx.lineTo(x, py);
    });
    ctx.strokeStyle = "rgba(180,180,180,0.55)";
    ctx.lineWidth = 1;
    ctx.stroke();

    const xStart = (start / Math.max(1, n - 1)) * width;
    const xEnd = (end / Math.max(1, n - 1)) * width;

    // Selection window highlight.
    ctx.fillStyle = "rgba(249,115,22,0.10)";
    ctx.fillRect(xStart, 0, xEnd - xStart, h);

    // Dim the unselected regions.
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    if (xStart > 0) ctx.fillRect(0, 0, xStart, h);
    if (xEnd < width) ctx.fillRect(xEnd, 0, width - xEnd, h);

    // Re-stroke selected portion in orange.
    ctx.beginPath();
    linePoints.forEach((y, i) => {
      if (i < start || i > end) return;
      const x = (i / Math.max(1, n - 1)) * width;
      const py = 3 + y * (h - 6);
      if (i === start) ctx.moveTo(x, py);
      else ctx.lineTo(x, py);
    });
    ctx.strokeStyle = "#f97316";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Handles — chunky bars with a centre grip line + hover/drag glow.
    const drawHandle = (x, side) => {
      const isHot = dragging === side || hoverHandle === side;
      // Soft glow when interactive.
      if (isHot) {
        ctx.shadowColor = "#f97316";
        ctx.shadowBlur = 8;
      }
      ctx.fillStyle = isHot ? "#ffb763" : "#f97316";
      ctx.fillRect(x - HANDLE_W / 2, 0, HANDLE_W, h);
      ctx.shadowBlur = 0;
      // Centre grip line.
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fillRect(x - 0.5, 6, 1, h - 12);
      // Two faint side grips (visual texture, like CoinGlass).
      ctx.fillStyle = "rgba(0,0,0,0.28)";
      ctx.fillRect(x - 3, 8, 1, h - 16);
      ctx.fillRect(x + 2, 8, 1, h - 16);
    };
    drawHandle(xStart, "left");
    drawHandle(xEnd, "right");
  }, [width, n, linePoints, start, end, dragging, hoverHandle, tickLabels]);

  const xToIdx = (x) => {
    if (!width || n <= 1) return 0;
    return clamp(Math.round((x / width) * (n - 1)), 0, n - 1);
  };

  const handleAtX = (x) => {
    const xStart = (start / Math.max(1, n - 1)) * width;
    const xEnd = (end / Math.max(1, n - 1)) * width;
    if (Math.abs(x - xStart) <= HANDLE_HIT_PX) return "left";
    if (Math.abs(x - xEnd) <= HANDLE_HIT_PX) return "right";
    if (x >= xStart && x <= xEnd) return "window";
    return null;
  };

  const onPointerDown = (e) => {
    if (!ref.current || !n) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const target = handleAtX(x);
    if (target === "left" || target === "right" || target === "window") {
      setDragging(target);
      dragStateRef.current = { startX: x, startRange: [start, end] };
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    } else {
      // Click outside the window → recenter.
      const span = end - start;
      const newStart = clamp(xToIdx(x) - Math.floor(span / 2), 0, n - 1 - span);
      onChange([newStart, newStart + span]);
    }
  };

  const onPointerMove = (e) => {
    if (!ref.current || !width || !n) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (!dragging) {
      // Update hover state only when idle so the cursor + handle glow
      // hint that the bars are draggable.
      const h = handleAtX(x);
      setHoverHandle(h === "left" || h === "right" ? h : null);
      return;
    }
    const idx = xToIdx(x);
    if (dragging === "left") {
      onChange([clamp(idx, 0, end - MIN_RANGE), end]);
    } else if (dragging === "right") {
      onChange([start, clamp(idx, start + MIN_RANGE, n - 1)]);
    } else if (dragging === "window") {
      const ds = dragStateRef.current;
      if (!ds) return;
      const dx = x - ds.startX;
      const dIdx = Math.round((dx / width) * (n - 1));
      const span = ds.startRange[1] - ds.startRange[0];
      const newStart = clamp(ds.startRange[0] + dIdx, 0, n - 1 - span);
      onChange([newStart, newStart + span]);
    }
  };

  const onPointerUp = () => {
    setDragging(null);
    dragStateRef.current = null;
  };

  if (!n) return null;

  // Cursor: ew-resize over handles, grab/grabbing on the window.
  const cursor = dragging === "window"
    ? "grabbing"
    : hoverHandle
    ? "ew-resize"
    : "ew-resize";

  return (
    <div
      ref={ref}
      className="relative w-full select-none"
      style={{ borderTop: "1px solid rgba(249,115,22,0.18)" }}
      data-testid="liqmap-range-brush"
    >
      {/* Time ticks row — evenly spaced labels along the full timeline,
          uses the same locale-aware formatter pattern as the rest of the
          Liquidity Map (HeatmapCanvas:677, CascadeRecentDrawer, etc.). */}
      <div
        className="relative w-full font-mono"
        style={{ height: LABEL_ROW_H, color: "rgba(180,180,180,0.55)", fontSize: 9 }}
        data-testid="liqmap-brush-ticks"
        aria-hidden="true"
      >
        {tickLabels.map(({ idx, label, t }, i) => {
          const xPct = n <= 1 ? 0 : (idx / (n - 1)) * 100;
          // Edge-anchor labels near the borders so they don't clip,
          // centre the rest. Because the ticks now align to clean clock
          // boundaries (not necessarily the very first/last candle),
          // the first/last labels can sit a few % inside — they still
          // get the same anchoring treatment when they're within 5% of
          // either edge.
          const transform = xPct < 5
            ? "translateX(0)"
            : xPct > 95
            ? "translateX(-100%)"
            : "translateX(-50%)";
          return (
            <span
              key={`${idx}-${t}`}
              className="absolute top-0 whitespace-nowrap"
              style={{ left: `${xPct}%`, transform, lineHeight: `${LABEL_ROW_H}px` }}
            >
              {label}
            </span>
          );
        })}
      </div>
      <div
        className="relative w-full"
        style={{ height: CANVAS_H, cursor, touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHoverHandle(null)}
        title="Arrastrá los extremos para acotar el rango temporal del heatmap"
      >
        <canvas ref={canvasRef} className="block" />
      </div>
    </div>
  );
}
